import { ChecklistTemplate } from './types';

/**
 * Plantilla maestra de revisión para sistemas de Videovigilancia (CCTV / VSS).
 * Basada en:
 *   - UNE EN 62676-1 a 62676-4 (sistemas de videovigilancia para aplicaciones de seguridad).
 *   - RD 2364/1994 Reglamento de Seguridad Privada y Orden INT/316/2011.
 *   - Ley 5/2014 de Seguridad Privada.
 *   - Reglamento (UE) 2016/679 (RGPD) y LO 3/2018 (LOPDGDD) — tratamiento de imágenes.
 *   - Instrucción 1/2006 AEPD sobre videovigilancia.
 *
 * Periodicidades aplicadas:
 *   T = Trimestral (revisión preventiva básica)
 *   S = Semestral (revisión técnica intermedia)
 *   A = Anual (revisión completa, normativa y RGPD).
 */
export const CCTV_TEMPLATE: ChecklistTemplate = {
  tipo_sistema: 'CCTV',
  titulo: 'Sistema de Videovigilancia (CCTV / VSS) — UNE EN 62676',
  protocolos: [
    {
      id: 'camaras',
      titulo: '1. Cámaras de videovigilancia',
      cabecera: [
        { key: 'cantidad_total', label: 'Nº total de cámaras', tipo: 'number' },
        { key: 'interior_cantidad', label: 'Cámaras interior', tipo: 'number' },
        { key: 'exterior_cantidad', label: 'Cámaras exterior', tipo: 'number' },
        { key: 'fijas_cantidad', label: 'Cámaras fijas', tipo: 'number' },
        { key: 'ptz_cantidad', label: 'Cámaras PTZ / domos motorizadas', tipo: 'number' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'resolucion', label: 'Resolución (MP / 1080p / 4K)' },
        { key: 'tipo_lente', label: 'Tipo de lente (fija / varifocal / motorizada)' },
        { key: 'ir_iluminacion', label: 'Iluminación IR / luz blanca' },
        { key: 'grado_ip', label: 'Grado IP / IK (intemperie)' },
      ],
      preguntas: [
        { id: 'C.1.1', texto: '¿Disponen las cámaras de marcado CE y son conformes con UNE EN 62676-2 / 62676-4?', periodicidad: ['A'], unidades: true },
        { id: 'C.1.2', texto: '¿La ubicación y orientación de cada cámara cubre las zonas definidas en el proyecto?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'C.1.3', texto: '¿La fijación, soportes y cajas estancas se encuentran en buen estado, sin holguras ni corrosión?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'C.1.4', texto: '¿Se ha realizado limpieza de óptica, carcasa y cúpula de cada cámara?', periodicidad: ['S','A'], unidades: true },
        { id: 'C.1.5', texto: '¿El enfoque, encuadre y nivel de zoom son los correctos para identificar / reconocer / detectar según uso previsto (UNE EN 62676-4)?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'C.1.6', texto: '¿La iluminación de la escena (diurna y nocturna IR) es suficiente para una imagen útil?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'C.1.7', texto: '¿El balance de blancos, WDR y exposición están correctamente ajustados a las condiciones de luz?', periodicidad: ['S','A'], unidades: true },
        { id: 'C.1.8', texto: '¿Las cámaras PTZ realizan correctamente movimiento, presets, rondas y autotracking (si aplica)?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'C.1.9', texto: '¿La carcasa exterior cumple el grado IP/IK necesario y los pasacables son estancos?', periodicidad: ['A'], unidades: true },
        { id: 'C.1.10', texto: '¿La sincronización horaria (NTP) de las cámaras es correcta y coincide con la del grabador?', periodicidad: ['T','S','A'] },
        { id: 'C.1.11', texto: '¿El firmware de cada cámara está actualizado conforme a recomendaciones del fabricante?', periodicidad: ['A'], unidades: true },
        { id: 'C.1.12', texto: '¿Se han cambiado los usuarios y contraseñas por defecto de cada cámara?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'grabacion',
      titulo: '2. Grabador (NVR / DVR / VMS)',
      cabecera: [
        { key: 'tipo_equipo', label: 'Tipo (NVR / DVR / servidor VMS)' },
        { key: 'marca', label: 'Marca' },
        { key: 'modelo', label: 'Modelo' },
        { key: 'firmware', label: 'Versión firmware / VMS' },
        { key: 'canales_total', label: 'Nº canales totales', tipo: 'number' },
        { key: 'canales_ocupados', label: 'Nº canales ocupados', tipo: 'number' },
        { key: 'capacidad_almacenamiento', label: 'Capacidad almacenamiento (TB)' },
        { key: 'configuracion_raid', label: 'Configuración RAID' },
        { key: 'dias_grabacion', label: 'Días de grabación garantizados', tipo: 'number' },
      ],
      preguntas: [
        { id: 'C.2.1', texto: '¿El grabador / VMS es conforme con UNE EN 62676-2 y dispone de marcado CE?', periodicidad: ['A'] },
        { id: 'C.2.2', texto: '¿Está instalado en armario o sala con acceso restringido, ventilación adecuada y libre de polvo?', periodicidad: ['T','S','A'] },
        { id: 'C.2.3', texto: '¿La fecha y hora del grabador son correctas y se sincronizan por NTP?', periodicidad: ['T','S','A'] },
        { id: 'C.2.4', texto: '¿Se ha comprobado el correcto funcionamiento de los discos duros (S.M.A.R.T., estado RAID)?', periodicidad: ['T','S','A'] },
        { id: 'C.2.5', texto: '¿La grabación garantiza el periodo de conservación legal (máx. 30 días — LOPDGDD)?', periodicidad: ['T','S','A'] },
        { id: 'C.2.6', texto: '¿Está correctamente configurada la grabación continua, por evento o por detección de movimiento según uso?', periodicidad: ['S','A'] },
        { id: 'C.2.7', texto: '¿Se ha verificado la calidad y fluidez de la grabación (resolución, fps y bitrate) en cada canal?', periodicidad: ['T','S','A'], unidades: true },
        { id: 'C.2.8', texto: '¿La exportación de imágenes a soporte externo funciona correctamente y conserva metadatos (hora, cámara)?', periodicidad: ['S','A'] },
        { id: 'C.2.9', texto: '¿Existen mecanismos de integridad / firma de las grabaciones que garanticen su no alteración?', periodicidad: ['A'] },
        { id: 'C.2.10', texto: '¿El firmware / software VMS está actualizado y se han aplicado parches de seguridad?', periodicidad: ['A'] },
        { id: 'C.2.11', texto: '¿Se ha cambiado el usuario y contraseña por defecto del grabador y existen perfiles diferenciados por usuario?', periodicidad: ['A'] },
        { id: 'C.2.12', texto: '¿Existe registro de accesos al sistema (logs) y se conservan los eventos suficientes para auditoría?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'monitorizacion',
      titulo: '3. Monitorización y visualización',
      cabecera: [
        { key: 'monitores_cantidad', label: 'Nº monitores', tipo: 'number' },
        { key: 'resolucion_monitor', label: 'Resolución monitor' },
        { key: 'puesto_control', label: 'Ubicación puesto de control' },
        { key: 'spot_publico', label: 'Existencia de monitor público (cartel informativo)' },
      ],
      preguntas: [
        { id: 'C.3.1', texto: '¿La visualización en directo se realiza con calidad suficiente y sin retardos significativos?', periodicidad: ['T','S','A'] },
        { id: 'C.3.2', texto: '¿La búsqueda y reproducción de grabaciones funciona correctamente desde el puesto de control?', periodicidad: ['T','S','A'] },
        { id: 'C.3.3', texto: '¿Los monitores están ubicados de modo que solo personal autorizado pueda visualizar las imágenes?', periodicidad: ['A'] },
        { id: 'C.3.4', texto: '¿Si existe acceso remoto (app / web), se realiza mediante VPN, HTTPS u otro canal cifrado?', periodicidad: ['A'] },
        { id: 'C.3.5', texto: '¿Los usuarios remotos tienen permisos diferenciados y trazabilidad de accesos?', periodicidad: ['A'] },
        { id: 'C.3.6', texto: '¿Se ha probado la visualización remota desde dispositivo móvil / cliente externo con resultado correcto?', periodicidad: ['S','A'] },
      ],
    },
    {
      id: 'red_alimentacion',
      titulo: '4. Red, alimentación y SAI',
      cabecera: [
        { key: 'switch_poe', label: 'Switch PoE (marca / modelo)' },
        { key: 'puertos_poe', label: 'Nº puertos PoE', tipo: 'number' },
        { key: 'consumo_poe', label: 'Consumo PoE total (W)', tipo: 'number' },
        { key: 'sai_marca', label: 'SAI marca' },
        { key: 'sai_modelo', label: 'SAI modelo' },
        { key: 'sai_autonomia', label: 'Autonomía SAI estimada (min)', tipo: 'number' },
        { key: 'red_dedicada', label: 'Red dedicada para CCTV (sí/no)' },
      ],
      preguntas: [
        { id: 'C.4.1', texto: '¿El cableado estructurado (UTP/FTP, fibra) y los conectores se encuentran en buen estado y correctamente etiquetados?', periodicidad: ['A'] },
        { id: 'C.4.2', texto: '¿La red del sistema CCTV está segmentada (VLAN o física) y aislada del resto de la red corporativa?', periodicidad: ['A'] },
        { id: 'C.4.3', texto: '¿Los switches PoE funcionan correctamente, no presentan errores y disponen de potencia suficiente para todas las cámaras?', periodicidad: ['T','S','A'] },
        { id: 'C.4.4', texto: '¿La conexión a Internet / red exterior está protegida mediante firewall y reglas restrictivas?', periodicidad: ['A'] },
        { id: 'C.4.5', texto: '¿El SAI alimenta grabador, switches PoE y cámaras críticas con autonomía suficiente?', periodicidad: ['T','S','A'] },
        { id: 'C.4.6', texto: '¿Se ha comprobado la autonomía real del SAI mediante corte de alimentación controlado?', periodicidad: ['A'] },
        { id: 'C.4.7', texto: '¿El estado y antigüedad de las baterías del SAI están dentro del periodo de vida útil del fabricante?', periodicidad: ['S','A'] },
        { id: 'C.4.8', texto: '¿Se ha verificado la puesta a tierra del rack, grabador y equipos activos?', periodicidad: ['A'] },
      ],
    },
    {
      id: 'integraciones',
      titulo: '5. Integraciones y videoverificación',
      cabecera: [
        { key: 'integracion_intrusion', label: 'Integración con sistema de intrusión (sí/no)' },
        { key: 'integracion_accesos', label: 'Integración con control de accesos (sí/no)' },
        { key: 'cra_videoverificacion', label: 'CRA con videoverificación (sí/no)' },
        { key: 'analitica_video', label: 'Analítica de vídeo activa' },
      ],
      preguntas: [
        { id: 'C.5.1', texto: '¿La integración con el sistema de intrusión y/o accesos funciona correctamente (eventos y vídeo asociado)?', periodicidad: ['T','S','A'] },
        { id: 'C.5.2', texto: '¿Si existe videoverificación a CRA, se ha realizado prueba real de envío y visualización en CRA?', periodicidad: ['T','S','A'] },
        { id: 'C.5.3', texto: '¿La analítica de vídeo (intrusión perimetral, conteo, merodeo, etc.) genera alarmas con tasa aceptable de falsos positivos?', periodicidad: ['S','A'] },
        { id: 'C.5.4', texto: '¿Se han revisado y ajustado las zonas de detección / máscaras de privacidad tras los últimos cambios en la escena?', periodicidad: ['S','A'] },
      ],
    },
    {
      id: 'lopd_rgpd',
      titulo: '6. Protección de datos, normativa y documentación',
      cabecera: [
        { key: 'responsable_tratamiento', label: 'Responsable del tratamiento' },
        { key: 'rat_actualizado', label: 'Registro de Actividades de Tratamiento actualizado (sí/no)' },
        { key: 'dias_conservacion', label: 'Días de conservación configurados', tipo: 'number' },
      ],
      preguntas: [
        { id: 'C.6.1', texto: '¿Existen carteles informativos de zona videovigilada visibles en cada acceso, conforme al modelo AEPD?', periodicidad: ['T','S','A'] },
        { id: 'C.6.2', texto: '¿Las cámaras están enfocadas exclusivamente a la zona privada del responsable, sin captar vía pública ni espacios ajenos más allá de lo imprescindible?', periodicidad: ['T','S','A'] },
        { id: 'C.6.3', texto: '¿Existen máscaras de privacidad sobre zonas que no deban ser grabadas (viviendas colindantes, vía pública)?', periodicidad: ['S','A'] },
        { id: 'C.6.4', texto: '¿No se captan imágenes con audio salvo casos justificados y debidamente informados?', periodicidad: ['A'] },
        { id: 'C.6.5', texto: '¿El periodo de conservación de las imágenes es ≤ 30 días, salvo conservación por incidencia denunciada?', periodicidad: ['T','S','A'] },
        { id: 'C.6.6', texto: '¿Está incorporada la videovigilancia al Registro de Actividades de Tratamiento (art. 30 RGPD)?', periodicidad: ['A'] },
        { id: 'C.6.7', texto: '¿Está informado el personal sobre el sistema de videovigilancia laboral (art. 89 LOPDGDD)?', periodicidad: ['A'] },
        { id: 'C.6.8', texto: '¿La empresa instaladora / mantenedora está inscrita en el Registro del Ministerio del Interior cuando proceda?', periodicidad: ['A'] },
        { id: 'C.6.9', texto: '¿Está disponible y actualizada la documentación del sistema (proyecto, planos, manual y libro de mantenimiento)?', periodicidad: ['A'] },
      ],
    },
  ],
};
